import {
  ApiAIPromptStatus,
  ApiDimension,
  ApiMetric,
  ApiPathStatus,
  ApiProject,
  ApiReadLanderResponse,
  ApiSimpleComponent,
} from "@incendium/api";
import {
  AnalyticsOutlined,
  AutorenewOutlined,
  CalendarTodayOutlined,
  DeleteOutline,
  EditOutlined,
  LanguageOutlined,
  MoreVert,
  OpenInNew,
  PreviewOutlined,
  ShowChartOutlined,
  UnpublishedOutlined,
  PublishOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  styled,
  CardMedia,
  BoxProps,
  useTheme,
  useMediaQuery,
  alpha,
} from "@mui/material";
import GlassCard from "Components/GlassCard/GlassCard";
import { useChartData, useFormatMetric } from "features/analytics";
import { useCallback, useMemo, useState } from "react";
import { ReactComponent as NoImage } from "Assets/Images/Illustrations/undraw_void_-3-ggu.svg";
import { ReactComponent as NoImage1 } from "Assets/Images/Illustrations/undraw_blank_canvas_re_2hwy.svg";
import { getDomain } from "features/subdomains/utils";
import { useHistory } from "react-router-dom";
import { normalizeLandingPath } from "features/lightningLanders/utils";
import { appendTrailingSlash } from "Helpers";
import { friendlyDateExtended } from "Helpers/dates";
import LanderPreviewDialog from "features/lightningLanders/components/LanderPreviewDialog";
import { LanderEditOptions } from "features/lightningLanders/types";
import moment from "moment-timezone";

const ImageSection = styled(({ children, ...rest }: BoxProps) => (
  <Box {...rest}>
    <Box className="lander-card-media">{children}</Box>
  </Box>
))(() => ({
  position: "relative",
  width: "100%",
  paddingTop: "56.25%",
  background:
    "linear-gradient(160deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.14) 70%)",
  borderBottom: "1px solid white",
  "& .lander-card-media": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    overflow: "hidden",
  },
  "& .lander-card-illustration": {
    width: "100%",
    height: "100%",
    display: "block",
    objectFit: "contain",
  },
  "& .lander-card-screenshot": {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
    bgcolor: "grey.100",
  },
}));

type StatusTone = "success" | "warning" | "default" | "error" | "info";

function StatusPill({ label, tone }: { label: string; tone: StatusTone }) {
  const theme = useTheme();
  const palette = {
    success: {
      bg: alpha(theme.palette.success.light, 0.1),
      color: theme.palette.success.dark,
      dot: theme.palette.success.main,
    },
    warning: {
      bg: alpha(theme.palette.warning.light, 0.1),
      color: theme.palette.warning.dark,
      dot: theme.palette.warning.main,
    },
    error: {
      bg: alpha(theme.palette.error.light, 0.1),
      color: theme.palette.error.dark,
      dot: theme.palette.error.main,
    },
    info: {
      bg: alpha(theme.palette.info.light, 0.1),
      color: theme.palette.info.dark,
      dot: theme.palette.info.main,
    },
    default: {
      bg: theme.palette.grey[100],
      color: theme.palette.text.secondary,
      dot: theme.palette.grey[500],
    },
  }[tone];

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        borderRadius: 999,
        bgcolor: palette.bg,
        color: palette.color,
        border: `1px solid ${palette.dot}`,
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: palette.dot,
          flexShrink: 0,
        }}
      />
      <Typography
        variant="caption"
        fontWeight={600}
        lineHeight={1}
        color={palette.color}
      >
        {label}
      </Typography>
    </Box>
  );
}

function MetaRow({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        sx={{
          color: "text.secondary",
          display: "flex",
          alignItems: "center",
          "& svg": { fontSize: 16 },
        }}
      >
        {icon}
      </Box>
      <Typography variant="body2" color="text.secondary" noWrap>
        {children}
      </Typography>
    </Stack>
  );
}

interface ILightningLanderCardProps {
  project: ApiProject;
  lander?: ApiReadLanderResponse;
  component: ApiSimpleComponent;
  onEdit: (l: ApiSimpleComponent, options?: LanderEditOptions) => void;
  onDelete: (l: ApiSimpleComponent) => void;
  onActivate: (l: ApiReadLanderResponse) => void;
  onDeactivate: (l: ApiReadLanderResponse) => void;
}

function LightningLanderCard({
  project,
  lander,
  component,
  onEdit,
  onDelete,
  onActivate,
  onDeactivate,
}: ILightningLanderCardProps) {
  const history = useHistory();
  const formatMetric = useFormatMetric();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [showPreview, setShowPreview] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const isAiPending =
    component?.prompt?.status === ApiAIPromptStatus.AI_PROMPT_STATUS_PENDING;
  const isActive = lander?.status === ApiPathStatus.PATH_STATUS_ACTIVE;
  const isDeleting = lander?.status === ApiPathStatus.PATH_STATUS_DELETING;
  const hasScreenshot = !!lander?.screenshotUrl && lander.screenshotUrl !== "";
  const canActivate = !!lander?.id && !!lander?.path && !!lander?.url;

  const path = useMemo(
    () => normalizeLandingPath(lander?.path),
    [lander?.path],
  );

  const domain = useMemo(
    () => (lander?.url ? getDomain(lander.url) : ""),
    [lander?.url],
  );

  const attributes = useMemo(
    () => [
      { key: ApiDimension.DIMENSION_PATH, value: path },
      { key: ApiDimension.DIMENSION_PATH, value: `${path}/` },
      { key: ApiDimension.DIMENSION_DOMAIN, value: domain },
    ],
    [path, domain],
  );

  const dateOverride = useMemo(
    () => ({ from: undefined, to: undefined, lastNDays: 90 }),
    [],
  );

  const chart = useMemo(
    () => ({
      name: "",
      dimension: [],
      yAxisKeys: [{ fields: [ApiMetric.METRIC_PAGEVIEWS_COUNT] }],
      attributes,
      includeEmpty: false,
    }),
    [attributes],
  );

  const { chartData } = useChartData(project, chart, dateOverride);

  const pageviews = Number(
    chartData?.data[0]?.[ApiMetric.METRIC_PAGEVIEWS_COUNT] ?? 0,
  );

  const status = useMemo((): {
    label: string;
    tone: StatusTone;
    tooltip: string;
  } => {
    if (isAiPending) {
      return {
        label: "Generating",
        tone: "info",
        tooltip:
          "AI is generating this landing page. It will be ready shortly.",
      };
    }
    if (isDeleting) {
      return {
        label: "Deleting",
        tone: "error",
        tooltip: "This landing page is being deleted.",
      };
    }
    if (lander?.pending) {
      if (isActive) {
        return {
          label: "Publishing",
          tone: "info",
          tooltip: "Publish has been requested and is still being processed.",
        };
      }
      return {
        label: "Unpublishing",
        tone: "info",
        tooltip: "Unpublish has been requested and is still being processed.",
      };
    }
    if (isActive && component?.pending) {
      return {
        label: "Unpublished edits",
        tone: "warning",
        tooltip:
          "This page is live, but you have edits that have not been published yet.",
      };
    }
    if (isActive) {
      return {
        label: "Live",
        tone: "success",
        tooltip: "This landing page is published and live.",
      };
    }
    if (!lander?.id || !lander?.url) {
      return {
        label: "Setup Required",
        tone: "warning",
        tooltip: "Set a path and domain before you can activate this page.",
      };
    }
    return {
      label: "Inactive",
      tone: "default",
      tooltip: "This landing page is not published yet.",
    };
  }, [
    isAiPending,
    isDeleting,
    lander?.pending,
    isActive,
    component?.pending,
    lander?.id,
    lander?.url,
  ]);

  const createdLabel = useMemo(() => {
    if (!component.createdAt) {
      return null;
    }
    const created = moment(component.createdAt);
    if (!created.isValid()) {
      return null;
    }
    return created.fromNow();
  }, [component.createdAt]);

  const toAnalytics = useCallback(() => {
    history.push(
      `/clients/${project.clientId}/projects/${project.id}/analyse/optimization/lightning-landing-pages/${lander?.id}`,
    );
  }, [project, lander, history]);

  const handleClosePreview = useCallback(() => {
    setShowPreview(false);
  }, []);

  const imageStatus = useMemo((): {
    label: string;
    tone: StatusTone;
    tooltip: string;
  } | null => {
    if (isAiPending || !hasScreenshot) {
      return null;
    }
    if (lander?.pending) {
      return {
        label: isActive ? "Publishing" : "Unpublishing",
        tone: "info",
        tooltip: isActive
          ? "Publish has been requested and is still being processed."
          : "Unpublish has been requested and is still being processed.",
      };
    }
    if (isActive) {
      return {
        label: "Live",
        tone: "success",
        tooltip: "This landing page has been published.",
      };
    }
    return {
      label: "Not Published",
      tone: "warning",
      tooltip: "This landing page has not been published yet.",
    };
  }, [isAiPending, hasScreenshot, lander?.pending, isActive]);

  const closeMenu = () => setMenuAnchor(null);

  const openPreview = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (isAiPending) {
        return;
      }
      setShowPreview(true);
    },
    [isAiPending],
  );

  return (
    <GlassCard
      disabled={isDeleting}
      boxProps={{ p: 0 }}
      sx={{
        overflow: "hidden",
        height: "100%",
      }}
    >
      <Stack spacing={2} alignItems="stretch" sx={{ height: "100%" }}>
        <ImageSection
          onClick={() => !isAiPending && openPreview()}
          sx={{
            cursor: !isAiPending ? "pointer" : "default",
          }}
        >
          {hasScreenshot ? (
            <CardMedia
              component="img"
              className="lander-card-screenshot"
              image={lander!.screenshotUrl}
            />
          ) : (
            <>
              {((lander?.id as number) ?? 0) % 2 === 0 ? (
                <NoImage className="lander-card-illustration" />
              ) : (
                <NoImage1 className="lander-card-illustration" />
              )}
            </>
          )}

          {hasScreenshot &&
            !isAiPending &&
            (!isActive || !component.screenshotTaken) && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(0,0,0,0.4)",
                  pointerEvents: "none",
                }}
              >
                <Typography
                  variant="overline"
                  sx={{ color: "white", fontWeight: 600, letterSpacing: 2 }}
                >
                  {!component.screenshotTaken
                    ? "Updating preview"
                    : "Preview"}
                </Typography>
              </Box>
            )}

          {imageStatus && (
            <Box
              sx={{ position: "absolute", bottom: 10, right: 10, zIndex: 2 }}
            >
              <Tooltip arrow title={imageStatus.tooltip}>
                <Box component="span" sx={{ display: "inline-flex" }}>
                  <StatusPill
                    label={imageStatus.label}
                    tone={imageStatus.tone}
                  />
                </Box>
              </Tooltip>
            </Box>
          )}

          {isAiPending && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(0,0,0,0.45)",
                gap: 1,
                px: 2,
              }}
            >
              <AutorenewOutlined
                sx={{
                  color: "white",
                  animation: "spin 2s linear infinite",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
              <Typography variant="body2" color="white" fontWeight={500}>
                AI is generating…
              </Typography>
            </Box>
          )}
        </ImageSection>

        <Stack
          sx={{
            px: 2,
            pb: 2,
            flex: 1,
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Tooltip arrow title={status.tooltip}>
                <Box component="span" sx={{ display: "inline-flex" }}>
                  <StatusPill label={status.label} tone={status.tone} />
                </Box>
              </Tooltip>
              <IconButton
                size="small"
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                disabled={isAiPending}
              >
                <MoreVert fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={closeMenu}
              >
                <MenuItem
                  onClick={() => {
                    closeMenu();
                    openPreview();
                  }}
                  disabled={isAiPending}
                >
                  <PreviewOutlined fontSize="small" sx={{ mr: 1 }} />
                  Preview
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    closeMenu();
                    onEdit(component);
                  }}
                  disabled={isAiPending}
                >
                  <EditOutlined fontSize="small" sx={{ mr: 1 }} />
                  Edit
                </MenuItem>
                {isActive && lander?.url && (
                  <MenuItem
                    component="a"
                    href={appendTrailingSlash(lander.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeMenu}
                  >
                    <OpenInNew fontSize="small" sx={{ mr: 1 }} />
                    View page
                  </MenuItem>
                )}
                {isActive && lander && (
                  <MenuItem
                    onClick={() => {
                      closeMenu();
                      onDeactivate(lander);
                    }}
                  >
                    <UnpublishedOutlined fontSize="small" sx={{ mr: 1 }} />
                    Unpublish
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    closeMenu();
                    onDelete(component);
                  }}
                  disabled={isAiPending}
                  sx={{ color: "error.main" }}
                >
                  <DeleteOutline fontSize="small" sx={{ mr: 1 }} />
                  Delete
                </MenuItem>
              </Menu>
            </Stack>

            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {component?.name || "Untitled landing page"}
            </Typography>

            {lander?.url ? (
              <Tooltip arrow title={lander.url}>
                <Link
                  href={appendTrailingSlash(lander.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  noWrap
                  underline="hover"
                  sx={{ mt: 0.25, display: "block" }}
                >
                  {appendTrailingSlash(lander.url)}
                </Link>
              </Tooltip>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.25 }}
              >
                Path not set ·{" "}
                <Link
                  component="button"
                  variant="body2"
                  underline="hover"
                  onClick={() => onEdit(component, { control: "settings" })}
                  sx={{ verticalAlign: "baseline" }}
                >
                  Set it
                </Link>
              </Typography>
            )}

            <Stack spacing={1} sx={{ mt: 3, mb: 2 }}>
              {createdLabel && (
                <MetaRow icon={<CalendarTodayOutlined />}>
                  {isActive ? "Created" : "Updated"} {createdLabel}
                </MetaRow>
              )}
              {isActive && lander?.url && (
                <MetaRow icon={<LanguageOutlined />}>
                  Published {friendlyDateExtended(component.createdAt)}
                </MetaRow>
              )}
              {isActive && lander?.url && (
                <MetaRow icon={<ShowChartOutlined />}>
                  {formatMetric(ApiMetric.METRIC_PAGEVIEWS_COUNT, pageviews)}{" "}
                  visits
                </MetaRow>
              )}
              {component.lastUser?.name && !isActive && (
                <MetaRow icon={<EditOutlined />}>
                  Last edited by {component.lastUser.name}
                </MetaRow>
              )}
            </Stack>
          </Box>
          <Stack direction="row" spacing={2}>
            {isActive &&
            lander?.url &&
            component?.pending &&
            !lander?.pending ? (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNew />}
                  href={appendTrailingSlash(lander.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ flex: 1 }}
                >
                  View Page
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PublishOutlined />}
                  onClick={() => lander && onActivate(lander)}
                  sx={{ flex: 1 }}
                >
                  Publish Changes
                </Button>
              </>
            ) : isActive && lander?.url ? (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNew />}
                  href={appendTrailingSlash(lander.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ flex: 1 }}
                >
                  View Page
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AnalyticsOutlined />}
                  onClick={toAnalytics}
                  sx={{ flex: 1 }}
                >
                  Analytics
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PreviewOutlined />}
                  onClick={openPreview}
                  disabled={isAiPending}
                  sx={{ flex: 1 }}
                >
                  Preview
                </Button>
                <Tooltip
                  title={
                    !canActivate
                      ? "Set a path and domain before activating"
                      : ""
                  }
                  arrow
                  disableHoverListener={canActivate}
                >
                  <span style={{ flex: 1, display: "flex" }}>
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      startIcon={<PublishOutlined />}
                      disabled={!canActivate || isAiPending}
                      onClick={() => lander && onActivate(lander)}
                    >
                      Publish
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </Stack>
        </Stack>
      </Stack>

      <LanderPreviewDialog
        open={showPreview}
        onClose={handleClosePreview}
        component={component}
        title={`${lander?.path || component?.name} - Preview`}
        fullScreen={fullScreen}
      />
    </GlassCard>
  );
}

export default LightningLanderCard;
